import {
  ChevronLeft,
  ChevronRight,
  FilePlus2,
  FileText,
  Info,
  ListChecks,
  ListPlus,
  Moon,
  RefreshCw,
  Search,
  Sun,
  Sunrise,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { AboutInfo, Appearance, UpdateStatus } from '../../shared/desktop';
import type { LocalRecord, Note, Task } from '../../shared/model';

import { desktop, shortcutLabel } from '../lib/desktop';
import { workspace } from '../lib/engine';
import { goTo, notePath, pagePaths, taskPath } from '../lib/routes';
import { areaDestinations, moreDestinations, tabDestinations } from './Navigation';
import {
  Button,
  Disclosure,
  IconButton,
  IconTile,
  Modal,
  Palette,
  type PaletteItem,
  QuietButton,
} from './ui';

/**
 * The desktop app's chrome, drawn by the page as Querybara draws its own
 * (docs/desktop-app.md): the top bar is the window's title bar, with Back and
 * Forward at its left and the command center in its middle, which opens the
 * Go to palette; the About box carries the version, the updates and the
 * licences; a quiet notice says when an update is ready. None of it renders
 * in a browser.
 */

/** Back and Forward, as the Go menu's ⌘[ and ⌘]. */
export function HistoryButtons() {
  return (
    <div className="window-history">
      <IconButton label="Back" size="compact" onClick={() => history.back()}>
        <ChevronLeft aria-hidden="true" />
      </IconButton>
      <IconButton label="Forward" size="compact" onClick={() => history.forward()}>
        <ChevronRight aria-hidden="true" />
      </IconButton>
    </div>
  );
}

/**
 * The middle of the title bar, as VS Code's command center: where you are, in
 * a field that opens Go to. It is a native button on the specialized
 * inventory because it is drawn as a search field rather than as an action.
 */
export function CommandCenter({ title, onOpen }: { title: string; onOpen: () => void }) {
  return (
    <button
      type="button"
      data-ui="specialized"
      className="command-center"
      aria-label={`Go to a page, task or note — now on ${title}`}
      title={`Go to a page, task or note (${shortcutLabel('P')})`}
      onClick={onOpen}
    >
      <Search aria-hidden="true" />
      <span>{title}</span>
      <kbd>{shortcutLabel('P')}</kbd>
    </button>
  );
}

/** A row that can be ordered by what was finished and what changed last. */
type RankedItem = PaletteItem & { done?: boolean; updatedAt?: string };

const matches = (text: string, query: string) => {
  const haystack = text.toLowerCase(),
    needle = query.trim().toLowerCase();

  if (!needle) {
    return 1;
  }

  if (haystack.startsWith(needle)) {
    return 3;
  }

  if (haystack.split(/[\s/–-]+/).some(word => word.startsWith(needle))) {
    return 2;
  }

  return haystack.includes(needle) ? 1 : 0;
};

/**
 * Go to (⌘P): every page in the sidebar's order, open tasks and notes by
 * title, and the commands the menus also hold. Nothing from the vault but the
 * page itself: vault entries are never searched outside the vault.
 */
export function GoToPalette({
  open,
  onClose,
  records,
  dark,
  onAppearance,
  onNewNote,
  onSetOut,
  onAbout,
}: {
  open: boolean;
  onClose: () => void;
  records: LocalRecord[];
  dark: boolean;
  onAppearance: (appearance: Appearance) => void;
  onNewNote: () => void;
  onSetOut: () => void;
  onAbout: () => void;
}) {
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (open) {
      setQuery('');
    }
  }, [open]);

  const items = useMemo(() => {
    const pages: PaletteItem[] = [
      ...tabDestinations.filter(tab => tab.page !== 'More'),
      ...Object.values(areaDestinations),
      ...moreDestinations,
    ].map(destination => ({
      id: `page:${destination.page}`,
      group: 'Pages',
      title: destination.title,
      detail: 'detail' in destination ? (destination.detail as string) : undefined,
      icon: <IconTile icon={destination.icon} tone={destination.tone} size="small" />,
      run: () => goTo(pagePaths[destination.page]),
    }));

    const tasks: RankedItem[] = records
      .filter(r => r.kind === 'task' && !r.deleted && !(r.value as Task).trashed)
      .map(r => {
        const task = r.value as Task;

        return {
          id: `task:${r.id}`,
          group: 'Tasks',
          title: task.title,
          detail: task.done ? 'Done' : task.dropped ? 'Dropped' : task.today ? 'Today' : 'Inbox',
          icon: <IconTile icon={ListChecks} tone={task.today ? 'priority' : 'info'} size="small" />,
          run: () => goTo(taskPath(r.id)),
          done: task.done || Boolean(task.dropped),
          updatedAt: r.updatedAt,
        };
      });

    const notes: RankedItem[] = records
      .filter(r => r.kind === 'note' && !r.deleted && !(r.value as Note).trashed)
      .map(r => {
        const note = r.value as Note;

        return {
          id: `note:${r.id}`,
          group: 'Notes',
          title: note.title || 'Untitled',
          detail: note.area,
          icon: <IconTile icon={FileText} tone="focus" size="small" />,
          run: () => goTo(notePath(r.id)),
          updatedAt: r.updatedAt,
        };
      });

    const commands: PaletteItem[] = [
      {
        id: 'command:new-note',
        group: 'Commands',
        title: 'New note',
        detail: shortcutLabel('N'),
        icon: <IconTile icon={FilePlus2} tone="focus" size="small" />,
        run: onNewNote,
      },
      {
        id: 'command:set-out',
        group: 'Commands',
        title: 'Set out for today',
        detail: '⇧⌘O',
        icon: <IconTile icon={Sunrise} tone="priority" size="small" />,
        run: onSetOut,
      },
      {
        id: 'command:sync',
        group: 'Commands',
        title: 'Sync now',
        detail: '⌥⌘S',
        icon: <IconTile icon={RefreshCw} tone="success" size="small" />,
        run: () => void workspace.sync(),
      },
      {
        id: 'command:appearance',
        group: 'Commands',
        title: dark ? 'Light appearance' : 'Dark appearance',
        icon: <IconTile icon={dark ? Sun : Moon} tone="neutral" size="small" />,
        run: () => onAppearance(dark ? 'light' : 'dark'),
      },
      {
        id: 'command:about',
        group: 'Commands',
        title: 'About Cairn',
        icon: <IconTile icon={Info} tone="neutral" size="small" />,
        run: onAbout,
      },
    ];

    const needle = query.trim();

    if (!needle) {
      return [...pages, ...commands];
    }

    const ranked = (list: RankedItem[], limit = 50) =>
      list
        .map(item => ({ item, score: matches(item.title, needle) }))
        .filter(entry => entry.score > 0)
        .sort(
          (a, b) =>
            b.score - a.score ||
            Number(a.item.done ?? false) - Number(b.item.done ?? false) ||
            (b.item.updatedAt ?? '').localeCompare(a.item.updatedAt ?? ''),
        )
        .slice(0, limit)
        .map(entry => entry.item);

    const create: PaletteItem = {
      id: 'command:create-task',
      group: 'Commands',
      title: `Create task “${needle}”`,
      icon: <IconTile icon={ListPlus} tone="priority" size="small" />,
      run: () => {
        const id = crypto.randomUUID();

        void workspace
          .save(id, 'task', {
            title: needle.slice(0, 500),
            noteId: null,
            done: false,
            today: false,
            steps: [],
            stoppingPoint: '',
          })
          .then(() => goTo(taskPath(id)));
      },
    };

    return [
      ...ranked(pages),
      ...ranked(tasks, 6),
      ...ranked(notes, 6),
      ...ranked(commands),
      create,
    ];
  }, [records, query, dark, onAppearance, onNewNote, onSetOut, onAbout]);

  return (
    <Palette
      open={open}
      onClose={onClose}
      label="Go to"
      placeholder="Go to a page, task or note…"
      query={query}
      onQuery={setQuery}
      items={items}
      empty="Nothing matches."
    />
  );
}

/** The About box's sentence for where updates stand. */
export function updateText(status: UpdateStatus | undefined, version: string) {
  switch (status?.state) {
    case undefined:
      return 'Reading the update status…';
    case 'off':
      return status.reason;
    case 'idle':
      return 'Cairn looks for updates shortly after it starts and every four hours.';
    case 'checking':
      return 'Checking for updates…';
    case 'current':
      return `Cairn ${version} is the newest version. Checked at ${new Date(
        status.checkedAt,
      ).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.`;
    case 'downloading':
      return `Downloading Cairn ${status.version} — ${status.percent}%.`;
    case 'ready':
      return `Cairn ${status.version} is ready. Restart to install it, or it installs when you quit.`;
    case 'failed':
      return status.message;
  }
}

/** About Cairn: the app, its runtime, the server it talks to, updates and licences. */
export function AboutCairn({
  open,
  onClose,
  email,
  status,
}: {
  open: boolean;
  onClose: () => void;
  email?: string;
  status?: UpdateStatus;
}) {
  const [info, setInfo] = useState<AboutInfo | null>(null),
    [notices, setNotices] = useState<string | null>(null),
    [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      void desktop?.about().then(setInfo);
    }
  }, [open]);

  if (!open) {
    return null;
  }

  const host = info ? new URL(info.server).host : '';

  return (
    <Modal
      open
      onClose={onClose}
      title="About Cairn"
      closeLabel="Close About"
      className="about-cairn"
    >
      <div className="about-identity">
        <img src="/cairn.svg" alt="" width={64} height={64} draggable={false} />
        <div>
          <p className="about-name">Cairn for Mac</p>
          <p>Version {info?.version ?? '…'}</p>
          {info && (
            <p className="muted">
              Electron {info.versions.electron} · Chromium {info.versions.chrome} · Node.js{' '}
              {info.versions.node} · {info.os} {info.arch}
            </p>
          )}
        </div>
      </div>
      {info && (
        <p className="muted">
          Connected to <strong>{host}</strong>
          {email ? ` as ${email}` : ''}. The session is kept in your Mac’s Keychain and never
          reaches the page.
        </p>
      )}
      <section className="about-section">
        <h3>Updates</h3>
        <p role="status">{updateText(status, info?.version ?? '')}</p>
        <div className="button-row">
          {status?.state === 'ready' ? (
            <Button variant="primary" onClick={() => void desktop?.updates('restart')}>
              Restart to update
            </Button>
          ) : (
            <Button
              disabled={
                busy ||
                status?.state === 'off' ||
                status?.state === 'checking' ||
                status?.state === 'downloading'
              }
              onClick={() => {
                setBusy(true);
                void desktop?.updates('check').finally(() => setBusy(false));
              }}
            >
              Check for updates
            </Button>
          )}
        </div>
      </section>
      <Disclosure
        summary="Third-party licences"
        className="about-licences"
        onToggle={event => {
          if ((event.currentTarget as HTMLDetailsElement).open && notices === null) {
            void fetch('/third-party-notices.txt')
              .then(response => (response.ok ? response.text() : ''))
              .then(setNotices)
              .catch(() => setNotices(''));
          }
        }}
      >
        <p className="muted">
          Cairn is built on open-source software, listed below with each licence. The Electron
          runtime (MIT) carries the licences of Chromium and Node.js in LICENSES.chromium.html
          inside the application, and updates are fetched by electron-updater (MIT).
        </p>
        <pre className="about-notices">{notices ?? 'Reading the licences…'}</pre>
      </Disclosure>
    </Modal>
  );
}

/** "Cairn 0.2.0 is ready": in the corner, once per version, never taking focus. */
export function DesktopUpdateNotice({ status }: { status?: UpdateStatus }) {
  const [dismissed, setDismissed] = useState<string>();

  if (status?.state !== 'ready' || dismissed === status.version) {
    return null;
  }

  return (
    <aside className="update-notice" role="status">
      <span>
        <strong>Cairn {status.version} is ready.</strong> Restart now, or it installs when you quit.
      </span>
      <Button variant="primary" onClick={() => void desktop?.updates('restart')}>
        Restart now
      </Button>
      <QuietButton onClick={() => setDismissed(status.version)}>Later</QuietButton>
    </aside>
  );
}

/** The latest of anything that changes, for a listener registered once. */
export function useLatest<T>(value: T) {
  const ref = useRef(value);

  ref.current = value;

  return ref;
}
